import { useNavigate, useParams, Link } from 'react-router-dom'
import { Link as MuiLink, Typography } from '@mui/material'

import { useState, useContext, useEffect, useMemo, ReactNode } from 'react'
import { AlertCategoryType, CommunicationChannel, AlertDto, AlertSendStatus, AlertSendType, AlertTemplate, NotifiedCustomers, TemplateLanguage } from '../../interfaces/IAlert'
import { enqueueSnackbar } from 'notistack'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { getEstimatedDownTimeText, toDateTime } from '../../utils/ValueFormatters'
import AlertSteps from './AlertSteps'
import ResponsiveDivider from '../../components/Divider/ResponsiveDivider'
import { EventDto } from '../../interfaces/IEvent'
import { CustomContainer } from '../../components/Wrapper/CustomContainer'
import { EditableFieldsDate, EditableFieldsString } from '../../interfaces/IFields'
import RootBreadcrumb from '../../components/navigation/RootBreadcrumb'
import { buildCustomerAlertHistory } from './customerAlertHistory'

export interface CustomStep {
  label: string
  content: ReactNode
}

function CreateOrUpdateAlert() {
  const navigate = useNavigate()
  const { context } = useContext(EbAuthenticatedAppContext)
  const paramIds = useParams()
  const [templates, setTemplates] = useState<AlertTemplate[]>([])

  const [currentAlert, setCurrentAlert] = useState<AlertDto>({} as AlertDto)
  const [viewEvent, setViewEvent] = useState<EventDto>({} as EventDto)
  const [sendLoading, setSendLoading] = useState(false)
  const [customerLanguages, setCustomerLanguages] = useState<Record<string, string>>({})

  const handleGetAlert = async (alertId: string | undefined) => {
    var response = await context.restApiClient.buildRequest(`/api/event/${paramIds.id}/alert/${paramIds.alertId}`).deserializeResponseToJson<AlertDto>().getAsync()

    if (response.json) {
      setCurrentAlert(response.json)
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke se varsel {alertId}. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
      navigate(`/events/${paramIds.id}`)
    }
  }

  const handleGetEvent = async (eventId: string | undefined) => {
    var response = await context.restApiClient.buildRequest(`/api/get-event/${eventId}`).deserializeResponseToJson<EventDto>().getAsync()

    if (response.json) {
      setViewEvent(response.json)
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke se hendelse {eventId}. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
      navigate('/404')
    }
  }

  const handleGetTemplates = async () => {
    var response = await context.restApiClient.buildRequest(`/api/templates`).deserializeResponseToJson<AlertTemplate[]>().getAsync()

    if (response.json) {
      setTemplates(response.json)
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Klarte ikke hente maler. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })
    }
  }

  const handleGetCustomerLanguages = async (customerNumbers: string[]) => {
    if (customerNumbers.length === 0) return

    const response = await context.restApiClient
      .buildRequest(`/api/customer-language/lookup`)
      .serializeToJsonContent(customerNumbers)
      .deserializeResponseToJson<Record<string, string>>()
      .postAsync()

    if (response.json) {
      setCustomerLanguages(response.json)
    }
  }

  const handleSetCustomerLanguage = async (customerNumber: string, language: string) => {
    const response = await context.restApiClient
      .buildRequest(`/api/customer-language`)
      .serializeToJsonContent({ customerNumber, language })
      .putAsync()

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke lagre språk for kunde {customerNumber}. Prøv igjen.</Typography>, { variant: 'error' })
    } else {
      setCustomerLanguages((prev) => ({ ...prev, [customerNumber]: language }))
    }
  }

  const filterTemplates = (templates: AlertTemplate[]) => {
    let filteredTemplates: AlertTemplate[] = [...templates]

    if (currentAlert.notifiedCustomers.allCompanyCustomers) {
      filteredTemplates = templates.filter((template) => template.channel === CommunicationChannel.Sms)
      filteredTemplates = filteredTemplates.filter((template) => template.customerInformationNeeded !== true)
    }

    if (currentAlert.phoneNumbers && currentAlert.phoneNumbers.length > 0) {
      filteredTemplates = templates.filter((template) => template.channel === CommunicationChannel.Sms)
      filteredTemplates = filteredTemplates.filter((template) => template.customerInformationNeeded === false)
    }

    if (currentAlert.emailAddresses && currentAlert.emailAddresses.length > 0) {
      filteredTemplates = templates.filter((template) => template.channel === CommunicationChannel.Email)
      filteredTemplates = filteredTemplates.filter((template) => template.customerInformationNeeded === false)
    }

    if (currentAlert.template.id !== 0 && !filteredTemplates.some((template) => template.id === currentAlert.template.id)) {
      setCurrentAlert((prevState) => ({
        ...prevState,
        template: {
          ...prevState.template,
          id: 0,
          title: '',
          subject: '',
          editedSubject: '',
          text: '',
          editedText: '',
          channel: undefined,
          editableFields: {},
        },
        englishTemplate: undefined,
      }))
    }

    // English masters are auto-linked from their Norwegian counterpart (handleSelectTemplateChange),
    // never picked directly, so they must not appear in the dropdown.
    return filteredTemplates.filter((template) => template.language !== TemplateLanguage.English)
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()

    setSendLoading(true)

    if (currentAlert.id != null && paramIds.alertId != null) {
      var response = await context.restApiClient.buildRequest(`/api/event/${paramIds.id}/alert/update-alert`).serializeToJsonContent(currentAlert).putAsync()

      if (response.error) {
        setSendLoading(false)
        if (response.error.errorCode === 409) {
          return enqueueSnackbar(
            <Typography variant="body2">
              Planlagt sendetid er passert. Oppdater tidspunktet eller bytt til &quot;Send umiddelbart&quot; før du publiserer.
            </Typography>,
            { variant: 'warning' },
          )
        }
        return enqueueSnackbar(
          <Typography variant="body2">Noe gikk galt, kunne ikke oppdatere varsling. Prøv igjen eller kontakt systemansvarlig.</Typography>,
          { variant: 'error' },
        )
      } else {
        enqueueSnackbar(<Typography variant="body2">Varsel {currentAlert.id} oppdatert.</Typography>, { variant: 'success' })

        setSendLoading(false)
        return navigate(`/events/${paramIds.id}`)
      }
    } else {
      var response = await context.restApiClient.buildRequest(`/api/event/${paramIds.id}/alert/create`).serializeToJsonContent(currentAlert).postAsync()

      if (response.error) {
        setSendLoading(false)
        enqueueSnackbar(
          <Typography variant="body2">Noe gikk galt, kunne ikke fullføre varsling. Prøv igjen eller kontakt systemansvarlig.</Typography>,
          { variant: 'error' },
        )
      } else {
        currentAlert.status === AlertSendStatus.Draft
          ? enqueueSnackbar(<Typography variant="body2">Utkast lagret.</Typography>, { variant: 'success' })
          : currentAlert.type === AlertSendType.Immediate
            ? enqueueSnackbar(<Typography variant="body2">Varsel sendt.</Typography>, { variant: 'success' })
            : enqueueSnackbar(<Typography variant="body2">Varsel lagret.</Typography>, { variant: 'success' })

        setSendLoading(false)
        return navigate(`/events/${paramIds.id}`)
      }
    }
  }

  const customerAlertHistory = useMemo(() => buildCustomerAlertHistory(viewEvent.alerts), [viewEvent.alerts])

  const populateInitialCreateState = () => {
    const alertHistory = customerAlertHistory

    const unnotifiedCompany =
      viewEvent.affectedCustomers
        ?.filter((c) => c.customerType === 'Company' && !alertHistory.has(c.customerNumber))
        .map((c) => c.customerNumber) ?? []
    const unnotifiedSla =
      viewEvent.affectedCustomers
        ?.filter((c) => c.customerType === 'CompanyWithSla' && !alertHistory.has(c.customerNumber))
        .map((c) => c.customerNumber) ?? []
    const unnotifiedPrivate =
      viewEvent.affectedCustomers
        ?.filter((c) => c.customerType === 'Private' && !alertHistory.has(c.customerNumber))
        .map((c) => c.customerNumber) ?? []

    const hasUnnotifiedCustomers =
      unnotifiedCompany.length + unnotifiedSla.length + unnotifiedPrivate.length > 0

    const unnotifiedPhoneNumbers = !hasUnnotifiedCustomers
      ? viewEvent.affectedPhoneNumbers?.filter((p) => !alertHistory.has(p)) ?? []
      : []

    const unnotifiedEmailAddresses =
      !hasUnnotifiedCustomers && unnotifiedPhoneNumbers.length === 0
        ? viewEvent.affectedEmailAddresses?.filter((e) => !alertHistory.has(e)) ?? []
        : []

    setCurrentAlert((prevState: any) => ({
      ...prevState,
      eventId: paramIds.id,
      description: '',
      category: AlertCategoryType.CustomerAlert,
      type: AlertSendType.Immediate,
      status: AlertSendStatus.NotProcessed,
      phoneNumbers: unnotifiedPhoneNumbers,
      emailAddresses: unnotifiedEmailAddresses,
      notifiedCustomers: {
        companyCustomerIds: hasUnnotifiedCustomers ? unnotifiedCompany : [],
        companyWithSlaCustomerIds: hasUnnotifiedCustomers ? unnotifiedSla : [],
        privateCustomerIds: hasUnnotifiedCustomers ? unnotifiedPrivate : [],
        allCompanyCustomers: false,
      },
      notifiedCustomersCount: 0,
      isSent: false,
      createdOn: new Date(),
      template: {
        ...prevState.template,
        id: 0,
        title: '',
        subject: '',
        editedSubject: '',
        text: '',
        editedText: '',
        channel: undefined,
        editableFields: {},
      },
      englishTemplate: undefined,
    }))
  }

  const setAlertTitle = () => {
    if (paramIds.alertId) {
      return 'Endre varsel'
    } else {
      return 'Ny varsel'
    }
  }

  const handleSetSelectedCustomers = (selectedCustomers: NotifiedCustomers) => {
    setCurrentAlert((prevState) => ({
      ...prevState,
      notifiedCustomers: {
        companyCustomerIds: selectedCustomers.companyCustomerIds,
        companyWithSlaCustomerIds: selectedCustomers.companyWithSlaCustomerIds,
        privateCustomerIds: selectedCustomers.privateCustomerIds,
        allCompanyCustomers: selectedCustomers.allCompanyCustomers,
      },
      phoneNumbers: selectedCustomers.phoneNumbers || [],
      emailAddresses: selectedCustomers.emailAddresses || [],
    }))
  }

  const applyEventValuesToEditableFields = (template: AlertTemplate) => {
    if (EditableFieldsDate.startTimeDate in template.editableFields && viewEvent.eventDetails?.fromDate) {
      template.editableFields[EditableFieldsDate.startTimeDate] = viewEvent.eventDetails.fromDate
    }
    if (EditableFieldsDate.endTimeDate in template.editableFields && viewEvent.eventDetails?.toDate) {
      template.editableFields[EditableFieldsDate.endTimeDate] = viewEvent.eventDetails.toDate
    }
    if (EditableFieldsString.downTime in template.editableFields && viewEvent.eventDetails?.estimatedDowntime) {
      template.editableFields[EditableFieldsString.downTime] = viewEvent.eventDetails.estimatedDowntime
    }
  }

  const handleSelectTemplateChange = (value: Date | number | string | object | undefined) => {
    const selectedTemplate = templates.find((template) => template.id === value)
    if (!selectedTemplate) return

    applyEventValuesToEditableFields(selectedTemplate)

    // The English counterpart shares the linked TemplateKey; look it up from the full (unfiltered)
    // template list, since English masters are not offered in the channel-filtered dropdown.
    const linkedEnglishTemplate = selectedTemplate.templateKey
      ? templates.find(
          (t) =>
            t.templateKey === selectedTemplate.templateKey &&
            t.language === TemplateLanguage.English &&
            t.id !== selectedTemplate.id,
        )
      : undefined

    if (linkedEnglishTemplate) {
      applyEventValuesToEditableFields(linkedEnglishTemplate)
      if (!linkedEnglishTemplate.sender) {
        linkedEnglishTemplate.sender = selectedTemplate.sender
      }
    }

    setCurrentAlert((prevState) => ({
      ...prevState,
      channel: selectedTemplate.channel,
      template: selectedTemplate,
      englishTemplate: linkedEnglishTemplate,
      description: selectedTemplate.title,
    }))
  }

  const handleInputChange = (name: string, value: Date | string | object | undefined) => {
    setCurrentAlert((prevState) => ({
      ...prevState,
      [name]: value,
      template: {
        ...prevState.template,
        [name]: value,
      },
    }))
  }

  const handleTemplateInputChange = (name: string, value: Date | string | object | undefined) => {
    setCurrentAlert((prevState) => ({
      ...prevState,
      template: {
        ...prevState.template,
        [name]: value,
      },
    }))
  }

  const handleReplaceContentText = (name: string, value: Date | string) => {
    setCurrentAlert((prevState) => ({
      ...prevState,
      template: {
        ...prevState.template,
        editableFields: { ...prevState.template.editableFields, [name]: value },
      },
      // The editable fields are event facts (dates, downtime) shared by both bodies, so keep the
      // English body in sync with the same values.
      englishTemplate: prevState.englishTemplate
        ? { ...prevState.englishTemplate, editableFields: { ...prevState.englishTemplate.editableFields, [name]: value } }
        : prevState.englishTemplate,
    }))
  }

  const handleIsDraftChange = (isDraft: boolean) => {
    setCurrentAlert((prevState) => ({
      ...prevState,
      status: isDraft ? AlertSendStatus.Draft : AlertSendStatus.NotProcessed,
    }))
  }

  const handleEditorChange = (editorRef: any) => {
    if (editorRef && currentAlert.template.channel === CommunicationChannel.Email) {
      setCurrentAlert((prevState) => ({
        ...prevState,
        template: {
          ...prevState.template,
          text: editorRef.getContent(),
          editedText: editorRef.getContent(),
        },
      }))
    } else if (editorRef && currentAlert.template.channel === CommunicationChannel.Sms) {
      let text = editorRef.getContent({ format: 'text' })

      setCurrentAlert((prevState) => ({
        ...prevState,
        template: {
          ...prevState.template,
          text: text,
          editedText: text,
        },
      }))
    }
  }

  const handleEnglishEditorChange = (editorRef: any) => {
    if (!editorRef) return

    const content =
      currentAlert.englishTemplate?.channel === CommunicationChannel.Sms ? editorRef.getContent({ format: 'text' }) : editorRef.getContent()

    setCurrentAlert((prevState) => ({
      ...prevState,
      englishTemplate: prevState.englishTemplate
        ? { ...prevState.englishTemplate, text: content, editedText: content }
        : prevState.englishTemplate,
    }))
  }

  const handleEnglishTemplateInputChange = (name: string, value: string) => {
    setCurrentAlert((prevState) => ({
      ...prevState,
      englishTemplate: prevState.englishTemplate ? { ...prevState.englishTemplate, [name]: value } : prevState.englishTemplate,
    }))
  }

  useEffect(() => {
    if (Object.keys(viewEvent).length === 0) {
      handleGetEvent(paramIds.id)
    }
  }, [context.restApiClient, paramIds])

  useEffect(() => {
    if (paramIds.alertId && Object.keys(currentAlert).length === 0) {
      if (viewEvent.alerts) {
        const alert = viewEvent.alerts.find((alert) => alert.id === paramIds.alertId)

        if (alert) {
          handleGetAlert(paramIds.alertId)
        } else {
          enqueueSnackbar(
            <Typography variant="body2">VarselID {paramIds.alertId} finnes ikke på hendelsen. Prøv igjen eller kontakt systemansvarlig.</Typography>,
            { variant: 'error' },
          )

          navigate(`/events/${paramIds.id}`)
        }
      }
    } else {
      if (Object.keys(currentAlert).length === 0) {
        populateInitialCreateState()
      }
    }

    handleGetTemplates()
  }, [viewEvent])

  useEffect(() => {
    const customerNumbers = viewEvent.affectedCustomers?.map((c) => c.customerNumber) ?? []
    if (customerNumbers.length > 0) {
      handleGetCustomerLanguages(customerNumbers)
    }
  }, [viewEvent.affectedCustomers])

  const applyTokenReplacements = (template: AlertTemplate) => {
    let { text, subject } = template
    const editableFields = template.editableFields

    subject = subject?.replaceAll(EditableFieldsString.caseNumber, viewEvent.id)

    if (editableFields) {
      Object.keys(editableFields).forEach((editableFieldKey) => {
        let editableField = editableFields[editableFieldKey] as Date | string

        if (Object.values(EditableFieldsString).includes(editableFieldKey as EditableFieldsString) && editableField !== '') {
          if (editableFieldKey === EditableFieldsString.downTime) {
            editableField = getEstimatedDownTimeText(editableField as string, template.language)
          }

          text = text.replaceAll(`${editableFieldKey}`, editableField as string)
          if (subject && subject.includes(`${editableFieldKey}`)) {
            subject = subject.replaceAll(`${editableFieldKey}`, editableField as string)
          }
        } else if (Object.values(EditableFieldsDate).includes(editableFieldKey as EditableFieldsDate) && editableField !== '') {
          const value = toDateTime(new Date(editableField))
          text = text.replaceAll(`${editableFieldKey}`, value)
          if (subject && subject.includes(`${editableFieldKey}`)) {
            subject = subject.replaceAll(`${editableFieldKey}`, value)
          }
        } else {
          text = text.replaceAll(`${editableFieldKey}`, 'ukjent')
          if (subject && subject.includes(`${editableFieldKey}`)) {
            subject = subject.replaceAll(`${editableFieldKey}`, 'ukjent')
          }
        }
      })
    }

    return { editedSubject: subject, editedText: text }
  }

  useEffect(() => {
    if (currentAlert.template) {
      setCurrentAlert((prevState) => ({
        ...prevState,
        template: {
          ...prevState.template,
          ...applyTokenReplacements(prevState.template),
        },
        englishTemplate: prevState.englishTemplate
          ? { ...prevState.englishTemplate, ...applyTokenReplacements(prevState.englishTemplate) }
          : prevState.englishTemplate,
      }))
    }
  }, [
    currentAlert.template?.editableFields,
    currentAlert.template?.editedText,
    currentAlert.template?.subject,
    currentAlert.englishTemplate?.editableFields,
    currentAlert.englishTemplate?.editedText,
    currentAlert.englishTemplate?.subject,
  ])

  useEffect(() => {
    if (currentAlert.isSent) {
      navigate(`/events/${paramIds.id}`)
      enqueueSnackbar(<Typography variant="body2">Varsel {paramIds.alertId} er allerede sendt.</Typography>, { variant: 'warning' })
    }
  }, [currentAlert])

  return (
    <>
      {currentAlert.isSent === false && (
        <CustomContainer maxWidth={'xl'}>
          <RootBreadcrumb>
            <MuiLink component={Link} underline="always" color="inherit" to={`/events/${paramIds.id}`} sx={{ display: 'flex', alignItems: 'center' }}>
              {paramIds.id}
            </MuiLink>
            <Typography>{currentAlert.id ? currentAlert.id : setAlertTitle()}</Typography>
          </RootBreadcrumb>

          <Typography variant="h5" sx={{
            marginTop: 4
          }}>
            {currentAlert.id ? currentAlert.id : setAlertTitle()}
          </Typography>

          <ResponsiveDivider />

          <form onSubmit={handleSubmit}>
            <AlertSteps
              currentAlert={currentAlert}
              templates={filterTemplates(templates)}
              viewEvent={viewEvent}
              handleInputChange={handleInputChange}
              handleTemplateInputChange={handleTemplateInputChange}
              handleReplaceContentText={handleReplaceContentText}
              handleSelectTemplateChange={handleSelectTemplateChange}
              handleEditorChange={handleEditorChange}
              handleEnglishEditorChange={handleEnglishEditorChange}
              handleEnglishTemplateInputChange={handleEnglishTemplateInputChange}
              onSelectedCustomersChange={handleSetSelectedCustomers}
              onIsDraftChange={handleIsDraftChange}
              customerAlertHistory={customerAlertHistory}
              customerLanguages={customerLanguages}
              onSetCustomerLanguage={handleSetCustomerLanguage}
              sendLoading={sendLoading}
            />
          </form>
        </CustomContainer>
      )}
    </>
  );
}

export default CreateOrUpdateAlert
